import { useQueryClient } from "@tanstack/react-query";
import { browserPopupRedirectResolver, GoogleAuthProvider, onAuthStateChanged, signInWithPopup, signOut, type User } from "firebase/auth";
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { recordSession } from "../api/me";
import { auth } from "./firebase";

interface AuthContextValue {
  user: User | null;
  loading: boolean;
  signInWithGoogle: () => Promise<void>;
  signOutNow: () => Promise<void>;
  getToken: () => Promise<string>;
  /** Set when a parent is viewing a linked student's account (that student's uid). */
  actingAs: string | null;
  setActingAs: (studentUid: string | null) => void;
}

const ACTING_AS_KEY = "actingAs";

function readActingAs(): string | null {
  try {
    return sessionStorage.getItem(ACTING_AS_KEY);
  } catch {
    return null;
  }
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [actingAs, setActingAsState] = useState<string | null>(readActingAs);
  const queryClient = useQueryClient();

  useEffect(
    () =>
      onAuthStateChanged(auth, (next) => {
        setUser(next);
        setLoading(false);
      }),
    [],
  );

  const signInWithGoogle = useCallback(async () => {
    // Sign-in asks Google for basic profile only. School systems are connected separately in Settings.
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({ prompt: "select_account" });
    const result = await signInWithPopup(auth, provider, browserPopupRedirectResolver);
    try {
      await recordSession(await result.user.getIdToken());
    } catch {
      // The login audit must never block signing in.
    }
  }, []);

  const setActingAs = useCallback((studentUid: string | null) => {
    setActingAsState(studentUid);
    try {
      if (studentUid) sessionStorage.setItem(ACTING_AS_KEY, studentUid);
      else sessionStorage.removeItem(ACTING_AS_KEY);
    } catch {
      // Private browsing: remember only for this page load.
    }
  }, []);

  const signOutNow = useCallback(async () => {
    await signOut(auth);
    setActingAs(null);
    queryClient.clear();
  }, [queryClient, setActingAs]);

  const getToken = useCallback(async () => {
    if (!auth.currentUser) throw new Error("Not signed in");
    return auth.currentUser.getIdToken();
  }, []);

  const value = useMemo(
    () => ({ user, loading, signInWithGoogle, signOutNow, getToken, actingAs, setActingAs }),
    [user, loading, signInWithGoogle, signOutNow, getToken, actingAs, setActingAs],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
